// ./src/components/features/Embeddings/EmbeddingFormModal/EmbeddingFormModal.tsx
import React, { useState, useEffect } from "react";
import {
  Stack,
  Alert,
  Text,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Grid,
  Accordion,
  ActionIcon,
} from "@mantine/core";
import { Embedding, EmbeddingInput } from "@/utils/api";
import { AppModal, AppIcon } from "@/components/generic";
import { EmbeddingLayoutTypeSelector } from "@/components/generic/EmbeddingLayoutTypeSelector";
import {
  VenueSelector,
  RoomSelector,
  ThemeSelector,
  EmbeddingEventTemplateSelector,
  ScheduleTypeSelector,
  CalendarViewSelector,
  CalendarStartDaySelector,
  CategorSelector,
} from "@/components/features";

export interface EmbeddingFormModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Embedding to edit (if null/undefined, it's create mode) */
  embedding?: Embedding | null;

  /** Callback when embedding should be created */
  onCreateEmbedding?: (
    embeddingData: EmbeddingInput,
  ) => Promise<Embedding | null>;

  /** Callback when embedding should be updated */
  onUpdateEmbedding?: (
    embeddingId: string,
    embeddingData: EmbeddingInput,
  ) => Promise<Embedding | null>;

  /** Whether create operation is in progress */
  isCreating?: boolean;

  /** Whether update operation is in progress */
  isUpdating?: boolean;

  /** Last error from embedding operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

interface EmbeddingFormData {
  name: string;
  description: string;
  scheduleType: string;
  venueId: string | null;
  roomId: string | null;
  roomTypeId: string | null;
  floor: string;
  roomCategoryId?: string | null;
  eventCategoryId?: string | null;
  showPastHours: number | null;
  showUpcomingHours: number | null;
  layoutType: string;
  columnsDesktop: number | null;
  columnsTablet: number | null;
  calendarView: string;
  calendarStartDay: number | null;
  showWeekend: boolean;
  calendarEventLimit: number | null;
  eventTemplateId: string | null;
  maxEvents: number | null;
  showImages: boolean;
  showDescriptions: boolean;
  showLocation: boolean;
  showRoom: boolean;
  datetimeFormat: string;
  themeId: string | null;
  customCss: string;
  accessToken: string;
  allowedDomains: string;
}

const initialFormData: EmbeddingFormData = {
  name: "",
  description: "",
  scheduleType: "current_day",
  venueId: null,
  roomId: null,
  roomTypeId: null,
  floor: "",
  roomCategoryId: null,
  eventCategoryId: null,
  showPastHours: null,
  showUpcomingHours: 24,
  layoutType: "grid",
  columnsDesktop: 3,
  columnsTablet: 2,
  calendarView: "month",
  calendarStartDay: 0,
  showWeekend: true,
  calendarEventLimit: 10,
  eventTemplateId: null,
  maxEvents: 50,
  showImages: true,
  showDescriptions: true,
  showLocation: true,
  showRoom: true,
  datetimeFormat: "MMM DD, YYYY h:mm A",
  themeId: null,
  customCss: "",
  accessToken: "",
  allowedDomains: "",
};

// Generate a random UUID for access token
const generateAccessToken = (): string => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const EmbeddingFormModal: React.FC<EmbeddingFormModalProps> = ({
  opened,
  onClose,
  embedding,
  onCreateEmbedding,
  onUpdateEmbedding,
  isCreating = false,
  isUpdating = false,
  lastError,
  onClearError,
}) => {
  const [formData, setFormData] = useState<EmbeddingFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<any>({});

  const isEditMode = Boolean(embedding) && Boolean(embedding?.id);
  const isSubmitting = isCreating || isUpdating;

  // Reset form when modal opens/closes or embedding changes
  useEffect(() => {
    if (opened) {
      if (embedding) {
        // Edit mode - populate form with embedding data
        setFormData({
          name: embedding.name || "",
          description: embedding.description || "",
          scheduleType: embedding.scheduleType || "current_day",
          venueId: embedding.venueId || null,
          roomId: embedding.roomId || null,
          roomTypeId: embedding.roomTypeId || null,
          floor: embedding.floor || "",
          roomCategoryId: embedding.roomCategoryId || "",
          eventCategoryId: embedding.eventCategoryId || "",
          showPastHours: embedding.showPastHours ?? null,
          showUpcomingHours: embedding.showUpcomingHours ?? 24,
          layoutType: embedding.layoutType || "grid",
          columnsDesktop: embedding.columnsDesktop ?? 3,
          columnsTablet: embedding.columnsTablet ?? 2,
          calendarView: embedding.calendarView || "month",
          calendarStartDay: embedding.calendarStartDay ?? 0,
          showWeekend: embedding.showWeekend ?? true,
          calendarEventLimit: embedding.calendarEventLimit ?? 10,
          eventTemplateId: embedding.eventTemplateId || null,
          maxEvents: embedding.maxEvents ?? 50,
          showImages: embedding.showImages ?? true,
          showDescriptions: embedding.showDescriptions ?? true,
          showLocation: embedding.showLocation ?? true,
          showRoom: embedding.showRoom ?? true,
          datetimeFormat: embedding.datetimeFormat || "MMM DD, YYYY h:mm A",
          themeId: embedding.themeId || null,
          customCss: embedding.customCss || "",
          accessToken: embedding.accessToken || "",
          allowedDomains: embedding.allowedDomains || "",
        });
      } else {
        // Create mode - use defaults and generate new access token
        setFormData({
          ...initialFormData,
          accessToken: generateAccessToken(),
        });
      }

      setValidationErrors({});
      onClearError?.();
    }
  }, [opened, embedding, onClearError]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: any = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = "Embedding name is required";
    }

    if (!formData.layoutType) {
      newErrors.layoutType = "Layout type is required";
    }

    if (!formData.accessToken.trim()) {
      newErrors.accessToken = "Access token is required";
    }

    setValidationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle field changes
  const handleFieldChange = (field: keyof EmbeddingFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors((prev: any) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  // Handle regenerate access token
  const handleRegenerateToken = () => {
    const newToken = generateAccessToken();
    handleFieldChange("accessToken", newToken);
  };

  // Handle save
  const handleSubmit = async () => {
    onClearError?.();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare embedding data
      const embeddingData: EmbeddingInput = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        scheduleType: formData.scheduleType || null,
        venueId: formData.venueId || null,
        roomId: formData.roomId || null,
        roomTypeId: formData.roomTypeId || null,
        floor: formData.floor.trim() || null,
        roomCategoryId: formData.roomCategoryId || null,
        eventCategoryId: formData.eventCategoryId || null,
        showPastHours: formData.showPastHours,
        showUpcomingHours: formData.showUpcomingHours,
        layoutType: formData.layoutType || null,
        columnsDesktop: formData.columnsDesktop,
        columnsTablet: formData.columnsTablet,
        calendarView: formData.calendarView || null,
        calendarStartDay: formData.calendarStartDay,
        showWeekend: formData.showWeekend,
        calendarEventLimit: formData.calendarEventLimit,
        eventTemplateId: formData.eventTemplateId || null,
        maxEvents: formData.maxEvents,
        showImages: formData.showImages,
        showDescriptions: formData.showDescriptions,
        showLocation: formData.showLocation,
        showRoom: formData.showRoom,
        datetimeFormat: formData.datetimeFormat || null,
        themeId: formData.themeId || null,
        customCss: formData.customCss.trim() || null,
        accessToken: formData.accessToken.trim() || null,
        allowedDomains: formData.allowedDomains.trim() || null,
      };

      let result: Embedding | null = null;

      if (isEditMode && embedding && onUpdateEmbedding) {
        // Update existing embedding
        result = await onUpdateEmbedding(embedding.id || "", embeddingData);
      } else if (!isEditMode && onCreateEmbedding) {
        // Create new embedding
        result = await onCreateEmbedding(embeddingData);
      }

      // If successful, close the modal
      if (result) {
        onClose();
      }
    } catch (error) {
      console.error("Form submission error:", error);
    }
  };

  const handleCancel = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  // Get the error to display for a field
  const getDisplayError = (field: string): string | undefined => {
    return validationErrors[field];
  };

  // Render generic errors
  const renderGenericErrors = () => {
    if (!lastError) {
      return null;
    }

    return (
      <Alert
        color="red"
        icon={<AppIcon iconName="AlertCircle" size={16} />}
        title="Error"
      >
        <Text size="sm">{lastError}</Text>
      </Alert>
    );
  };

  return (
    <AppModal
      opened={opened}
      onClose={handleCancel}
      title={isEditMode ? "Edit Embedding" : "Create New Embedding"}
      size="xl"
      loading={isSubmitting}
      actions={[
        {
          label: "Cancel",
          variant: "secondary",
          onClick: handleCancel,
          disabled: isSubmitting,
        },
        {
          label: "Save",
          variant: "primary",
          iconName: "Check",
          onClick: handleSubmit,
          inProgress: isSubmitting,
        },
      ]}
    >
      <Stack gap="md">
        <Accordion multiple defaultValue={["basic", "schedule", "layout"]}>
          {/* Basic Information */}
          <Accordion.Item value="basic">
            <Accordion.Control>Basic Information</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <TextInput
                  label="Embedding Name"
                  placeholder="Enter embedding name"
                  value={formData.name}
                  onChange={(e) => handleFieldChange("name", e.target.value)}
                  error={getDisplayError("name")}
                  required
                  withAsterisk
                  size="md"
                />

                <Textarea
                  label="Description"
                  placeholder="Enter embedding description (optional)"
                  value={formData.description}
                  onChange={(e) =>
                    handleFieldChange("description", e.target.value)
                  }
                  minRows={2}
                  maxRows={4}
                  autosize
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Schedule Settings */}
          <Accordion.Item value="schedule">
            <Accordion.Control>Schedule Settings</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <ScheduleTypeSelector
                  label="Schedule Type"
                  placeholder="Select schedule type"
                  value={formData.scheduleType}
                  onChange={(val) => handleFieldChange("scheduleType", val)}
                  required
                />

                <VenueSelector
                  label="Venue"
                  value={formData.venueId}
                  onChange={(venueId) => handleFieldChange("venueId", venueId)}
                />

                <RoomSelector
                  label="Room"
                  value={formData.roomId}
                  onChange={(roomId) => handleFieldChange("roomId", roomId)}
                  venueId={formData.venueId}
                />

                <Grid>
                  <Grid.Col span={12}>
                    <TextInput
                      label="Floor"
                      placeholder="e.g., 1, 2, Ground"
                      value={formData.floor}
                      onChange={(e) =>
                        handleFieldChange("floor", e.target.value)
                      }
                      size="md"
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <CategorSelector
                      label="Room Category"
                      placeholder="Select room category"
                      value={formData.roomCategoryId}
                      onChange={(categoryId) =>
                        handleFieldChange("roomCategoryId", categoryId)
                      }
                      // disabled={disabled}
                      // size={size}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ base: 12, sm: 6 }}>
                    <CategorSelector
                      label="Event Category"
                      placeholder="Select event category"
                      value={formData.eventCategoryId}
                      onChange={(categoryId) =>
                        handleFieldChange("eventCategoryId", categoryId)
                      }
                      // disabled={disabled}
                      // size={size}
                    />
                  </Grid.Col>
                </Grid>

                <Grid>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Show Past Hours"
                      placeholder="Hours to show past events"
                      value={formData.showPastHours ?? undefined}
                      onChange={(value) =>
                        handleFieldChange("showPastHours", value)
                      }
                      min={0}
                      size="md"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Show Upcoming Hours"
                      placeholder="Hours to show upcoming events"
                      value={formData.showUpcomingHours ?? undefined}
                      onChange={(value) =>
                        handleFieldChange("showUpcomingHours", value)
                      }
                      min={1}
                      size="md"
                    />
                  </Grid.Col>
                </Grid>
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Layout Settings */}
          <Accordion.Item value="layout">
            <Accordion.Control>Layout Settings</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <EmbeddingLayoutTypeSelector
                  label="Layout Type"
                  value={formData.layoutType}
                  onChange={(value) => handleFieldChange("layoutType", value)}
                  error={getDisplayError("layoutType")}
                  required
                  withAsterisk
                />

                <Grid>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Columns (Desktop)"
                      value={formData.columnsDesktop ?? undefined}
                      onChange={(value) =>
                        handleFieldChange("columnsDesktop", value)
                      }
                      min={1}
                      max={6}
                      size="md"
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Columns (Tablet)"
                      value={formData.columnsTablet ?? undefined}
                      onChange={(value) =>
                        handleFieldChange("columnsTablet", value)
                      }
                      min={1}
                      max={4}
                      size="md"
                    />
                  </Grid.Col>
                </Grid>

                <CalendarViewSelector
                  label="Calendar View"
                  placeholder="e.g., month, week, day"
                  value={formData.calendarView}
                  onChange={(value) => handleFieldChange("calendarView", value)}
                  required
                />

                <Grid>
                  <Grid.Col span={6}>
                    <CalendarStartDaySelector
                      label="Calendar Start Day"
                      value={"" + formData.calendarStartDay}
                      onChange={(value) => {
                        handleFieldChange("calendarStartDay", Number(value));
                      }}
                      required
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <NumberInput
                      label="Calendar Event Limit"
                      value={formData.calendarEventLimit ?? undefined}
                      onChange={(value) =>
                        handleFieldChange("calendarEventLimit", value)
                      }
                      min={1}
                      size="md"
                    />
                  </Grid.Col>
                </Grid>

                <Switch
                  label="Show Weekend"
                  checked={formData.showWeekend}
                  onChange={(e) =>
                    handleFieldChange("showWeekend", e.currentTarget.checked)
                  }
                  size="md"
                />

                <NumberInput
                  label="Max Events"
                  placeholder="Maximum events to display"
                  value={formData.maxEvents ?? undefined}
                  onChange={(value) => handleFieldChange("maxEvents", value)}
                  min={1}
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Display Options */}
          <Accordion.Item value="display">
            <Accordion.Control>Display Options</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <EmbeddingEventTemplateSelector
                  label="Event Template"
                  value={formData.eventTemplateId}
                  onChange={(templateId: any) =>
                    handleFieldChange("eventTemplateId", templateId)
                  }
                  required
                />

                <Switch
                  label="Show Images"
                  checked={formData.showImages}
                  onChange={(e) =>
                    handleFieldChange("showImages", e.currentTarget.checked)
                  }
                  size="md"
                />

                <Switch
                  label="Show Descriptions"
                  checked={formData.showDescriptions}
                  onChange={(e) =>
                    handleFieldChange(
                      "showDescriptions",
                      e.currentTarget.checked,
                    )
                  }
                  size="md"
                />

                <Switch
                  label="Show Location"
                  checked={formData.showLocation}
                  onChange={(e) =>
                    handleFieldChange("showLocation", e.currentTarget.checked)
                  }
                  size="md"
                />

                <Switch
                  label="Show Room"
                  checked={formData.showRoom}
                  onChange={(e) =>
                    handleFieldChange("showRoom", e.currentTarget.checked)
                  }
                  size="md"
                />

                <TextInput
                  label="Date/Time Format"
                  placeholder="e.g., MMM DD, YYYY h:mm A"
                  value={formData.datetimeFormat}
                  onChange={(e) =>
                    handleFieldChange("datetimeFormat", e.target.value)
                  }
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Theme & Styling */}
          <Accordion.Item value="theme">
            <Accordion.Control>Theme & Styling</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <ThemeSelector
                  label="Theme"
                  value={formData.themeId}
                  onChange={(themeId: any) =>
                    handleFieldChange("themeId", themeId)
                  }
                  required
                />

                <Textarea
                  label="Custom CSS"
                  placeholder="Enter custom CSS code (optional)"
                  value={formData.customCss}
                  onChange={(e) =>
                    handleFieldChange("customCss", e.target.value)
                  }
                  minRows={4}
                  maxRows={10}
                  autosize
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      fontSize: "13px",
                    },
                  }}
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>

          {/* Security */}
          <Accordion.Item value="security">
            <Accordion.Control>Security</Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <TextInput
                  label="Access Token"
                  description="This token is required for embedding API access"
                  placeholder="Access token"
                  value={formData.accessToken}
                  onChange={(e) =>
                    handleFieldChange("accessToken", e.target.value)
                  }
                  error={getDisplayError("accessToken")}
                  required
                  withAsterisk
                  readOnly
                  rightSection={
                    <ActionIcon
                      variant="subtle"
                      onClick={handleRegenerateToken}
                      disabled={isSubmitting}
                    >
                      <AppIcon iconName="RotateClockwise2" size={16} />
                    </ActionIcon>
                  }
                  size="md"
                />

                <Textarea
                  label="Allowed Domains"
                  placeholder="Enter allowed domains (one per line or comma-separated)"
                  value={formData.allowedDomains}
                  onChange={(e) =>
                    handleFieldChange("allowedDomains", e.target.value)
                  }
                  minRows={3}
                  maxRows={6}
                  autosize
                  size="md"
                />
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>

        {/* Error display */}
        {renderGenericErrors()}
      </Stack>
    </AppModal>
  );
};
